import { ChangeEvent, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  EmailInputStyled,
  InputDescriptionStyled,
  LabelStyled,
} from 'approot/shared/styles/inputs.style';
import { LinkStyled } from 'approot/shared/styles/typography.style';
import { PUBLIC_SIGNIN, PUBLIC_STUDENTS } from 'global/public-routes.constants';
import validateEmailFormat from 'lib/validate/validate-email-format';
import validateMaxLength from 'lib/validate/validate-max-length';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { FlowSmallText } from './flow.style';
import { MAX_EMAIL_LENGTH } from 'lib/validate/validate.constants';
import { FlowEmailValidation_emailValidation } from './__generated__/FlowEmailValidation';
import { FlowEmailValidationMessages } from './flow-email-validation';
import { VALIDATION_MESSAGE_EMAIL_REQUIRED } from 'webclient.constants';
import { LinkButton } from 'approot/shared/styles/buttons/buttons';
import { INTERCOM_LAUNCH_TARGET_CLASS } from 'lib/intercom/intercom';
import { Box } from 'approot/shared/styles/box.style';
import { ValidSpacings } from 'approot/shared/styles/spacing.style';
import shouldUseSSO from 'approot/account/logic/should-use-sso';

export const EMAIL_FIELD = 'email';
export const CONFIRM_EMAIL_FIELD = 'confirmEmail';
export const PASSWORD_FIELD = 'password';

export const FlowSignInHelp = ({
  onClick,
  dataTestId,
}: {
  onClick?: () => void;
  dataTestId?: string;
}) => (
  <FlowSmallText marginBottom={0}>
    Already have an account?{' '}
    <LinkStyled
      to={PUBLIC_SIGNIN}
      data-selector="sign-in-link"
      onClick={onClick}
      data-testid={dataTestId}
    >
      Sign in here
    </LinkStyled>
  </FlowSmallText>
);

export const FlowStudentSignInHelp = () => (
  <FlowSmallText marginBottom={8}>
    Are you a student?{' '}
    <LinkStyled to={PUBLIC_STUDENTS}>Sign in here</LinkStyled>
  </FlowSmallText>
);

export const FlowEmailField = ({
  marginBottom,
  validationResult,
  hasError,
  onCheckIfIsDetNswEmail,
}: {
  marginBottom?: ValidSpacings;
  validationResult: FlowEmailValidation_emailValidation[] | undefined;
  hasError?: boolean;
  onCheckIfIsDetNswEmail?: (isDetNswEmail: boolean) => void;
}) => {
  const formMethods = useFormContext();
  const {
    register,
    formState: { errors, isSubmitted },
    watch,
    trigger,
    setValue,
  } = formMethods;

  const [showConfirmEmail, setShowConfirmEmail] = useState<boolean>(true);

  const { onChange: registerOnChange } = register(EMAIL_FIELD);

  return (
    <>
      <Box $marginBottom={24} $flexDirection="column">
        <LabelStyled data-selector="label-email" htmlFor={EMAIL_FIELD}>
          Education Email Address
        </LabelStyled>
        <EmailInputStyled
          id={EMAIL_FIELD}
          data-selector="email"
          {...register(EMAIL_FIELD, {
            validate: {
              emailFormat: value => validateEmailFormat(value),
              maxLength: value =>
                validateMaxLength(value, MAX_EMAIL_LENGTH, 'Email'),
            },
          })}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const isDetNswEmail = shouldUseSSO(e.target.value) || false;
            setValue(EMAIL_FIELD, e.target.value);
            onCheckIfIsDetNswEmail?.(isDetNswEmail);
            setShowConfirmEmail(!isDetNswEmail);
            isSubmitted && trigger(CONFIRM_EMAIL_FIELD);
            registerOnChange(e);
          }}
          aria-required
          aria-invalid={!!errors.email}
          aria-describedby={
            errors[EMAIL_FIELD]
              ? `flow-${EMAIL_FIELD}-error`
              : `flow-${EMAIL_FIELD}-description`
          }
          autoComplete="email"
          $hasError={!!errors.email}
        />
        {!Object.keys(errors).length && !hasError && !validationResult && (
          <InputDescriptionStyled data-selector="email-guidelines">
            <LinkButton
              type="button"
              className={INTERCOM_LAUNCH_TARGET_CLASS}
              aria-haspopup="dialog"
            >
              Contact us
            </LinkButton>{' '}
            if you don't have an education email address
          </InputDescriptionStyled>
        )}
        <ValidationMessage
          id={`flow-${EMAIL_FIELD}-error`}
          data-selector="flow-account-error-email"
          errors={errors}
          field={EMAIL_FIELD}
          suffix={
            errors?.[EMAIL_FIELD]?.message ===
              VALIDATION_MESSAGE_EMAIL_REQUIRED && (
              <>
                {' '}
                <LinkButton
                  error
                  type="button"
                  className={INTERCOM_LAUNCH_TARGET_CLASS}
                  aria-haspopup="dialog"
                >
                  Contact us
                </LinkButton>{' '}
                if you don’t have one
              </>
            )
          }
        />
        {validationResult && (
          <FlowEmailValidationMessages
            validationResult={validationResult}
            marginBottom={0}
          />
        )}
      </Box>

      {showConfirmEmail && (
        <Box $marginBottom={marginBottom || 24} $flexDirection="column">
          <LabelStyled
            data-selector="label-confirm-email"
            htmlFor={CONFIRM_EMAIL_FIELD}
          >
            Confirm Education Email Address
          </LabelStyled>
          <EmailInputStyled
            id={CONFIRM_EMAIL_FIELD}
            data-selector="confirm-email"
            {...register(CONFIRM_EMAIL_FIELD, {
              validate: value =>
                watch(EMAIL_FIELD) === value ||
                'Oops! These emails aren’t lining up. Can you double-check them?',
            })}
            aria-required
            aria-invalid={!!errors.confirmEmail}
            aria-describedby={
              errors[CONFIRM_EMAIL_FIELD]
                ? `flow-${CONFIRM_EMAIL_FIELD}-error`
                : `flow-${CONFIRM_EMAIL_FIELD}-description`
            }
            autoComplete="off"
            onPaste={e => e.preventDefault()}
            $hasError={!!errors.confirmEmail}
          />
          <ValidationMessage
            id={`flow-${CONFIRM_EMAIL_FIELD}-error`}
            errors={errors}
            field={CONFIRM_EMAIL_FIELD}
          />
        </Box>
      )}
    </>
  );
};
